import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, XCircle, Info, AlertTriangle, X } from "lucide-react";
import useFeedbackStore from "../../store/useFeedbackStore";

const AUTO_DISMISS_MS = 3000;

const TYPE_CONFIG = {
  success: {
    Icon: CheckCircle,
    containerClass:
      "bg-fill-green-50 dark:bg-fill-green-900/20 border-edge-green-200 dark:border-edge-green-700",
    iconClass: "text-ink-green-600 dark:text-ink-green-400",
    textClass: "text-ink-green-800 dark:text-ink-green-200",
  },
  error: {
    Icon: XCircle,
    containerClass:
      "bg-fill-red-50 dark:bg-fill-red-900/20 border-edge-red-200 dark:border-edge-red-700",
    iconClass: "text-ink-red-500 dark:text-ink-red-400",
    textClass: "text-ink-red-800 dark:text-ink-red-200",
  },
  warning: {
    Icon: AlertTriangle,
    containerClass:
      "bg-fill-yellow-50 dark:bg-fill-yellow-900/20 border-edge-yellow-200 dark:border-edge-yellow-700",
    iconClass: "text-ink-yellow-600 dark:text-ink-yellow-400",
    textClass: "text-ink-yellow-800 dark:text-ink-yellow-200",
  },
  info: {
    Icon: Info,
    containerClass:
      "bg-fill-blue-50 dark:bg-fill-blue-900/20 border-edge-blue-200 dark:border-edge-blue-700",
    iconClass: "text-ink-primary dark:text-ink-accent",
    textClass: "text-ink-blue-800 dark:text-ink-blue-200",
  },
};

const FeedbackBanner = () => {
  const feedback = useFeedbackStore((state) => state.feedback);
  const clearFeedback = useFeedbackStore((state) => state.clearFeedback);

  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(clearFeedback, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [feedback, clearFeedback]);

  const config = TYPE_CONFIG[feedback?.type] ?? TYPE_CONFIG.info;
  const { Icon, containerClass, iconClass, textClass } = config;

  return (
    <AnimatePresence>
      {feedback && (
        <motion.div
          key={feedback.message + feedback.type}
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="fixed top-4 right-4 z-[500] max-w-sm w-full"
        >
          <div
            className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg ${containerClass}`}
          >
            <Icon size={18} className={`shrink-0 mt-0.5 ${iconClass}`} />
            <p className={`flex-1 text-sm leading-snug ${textClass}`}>
              {feedback.message}
            </p>
            <button
              onClick={clearFeedback}
              className={`shrink-0 mt-0.5 opacity-60 hover:opacity-100 transition-opacity ${iconClass}`}
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeedbackBanner;
